import { TZDate } from "@date-fns/tz";
import {
    eachDayOfInterval,
    eachMonthOfInterval,
    endOfMonth,
    format,
    formatRelative,
    isValid,
    startOfDay,
    startOfMonth,
    subDays,
    subMonths,
} from 'date-fns';

/** Backend datetime "2026-07-03 15:02:19.477" — Safari requires ISO T separator */
export function normalizeBackendDateString(date: string): string {
    return date.includes('T') ? date : date.replace(' ', 'T');
}

export function parseBackendDate(date: string): Date {
    return new Date(normalizeBackendDateString(date));
}

export const formatDate = (date: string | null | undefined, dateFormat: string = 'HH:mm MM-dd') => {
    if (date == null || date === '') return '';
    const timezoned = new TZDate(
        normalizeBackendDateString(date),
        import.meta.env.VITE_TIMEZONE
    );
    if (!isValid(timezoned)) return date;
    return format(timezoned, dateFormat);
};

export function formatRelativeBackendDate(
    date: string,
    baseDate: Date | number = Date.now()
): string {
    const parsed = parseBackendDate(date);
    if (!isValid(parsed)) return date;
    return formatRelative(parsed, baseDate);
};

function nowInAppTimezone(): TZDate {
    return new TZDate(new Date(), import.meta.env.VITE_TIMEZONE);
}

/** Date keys for metrics charts: week=7 days, month=calendar month, global=12 months. */
export function metricsPeriodDateKeys(period: 'week' | 'month' | 'all_time'): string[] {
    const now = nowInAppTimezone();
    if (period === 'week') {
        const end = startOfDay(now);
        const start = subDays(end, 6);
        return eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'));
    }
    if (period === 'month') {
        return eachDayOfInterval({
            start: startOfMonth(now),
            end: endOfMonth(now),
        }).map(d => format(d, 'yyyy-MM-dd'));
    }
    const end = startOfMonth(now);
    const start = subMonths(end, 11);
    return eachMonthOfInterval({ start, end }).map(d => format(d, 'yyyy-MM'));
}

/** From/to window the widgets cover, mirroring the backend _period_bounds. */
export function metricsPeriodRangeLabel(
    period: 'week' | 'month' | 'all_time',
    isTeam: boolean
): string {
    const now = nowInAppTimezone();
    const to = format(now, 'MMM d, yyyy');
    if (period === 'week') return `${format(subDays(startOfDay(now), 6), 'MMM d, yyyy')} — ${to}`;
    if (period === 'month') return `${format(startOfMonth(now), 'MMM d, yyyy')} — ${to}`;
    if (!isTeam) return 'All time';
    return `${format(startOfMonth(subDays(now, 365)), 'MMM d, yyyy')} — ${to}`;
}
